/**
 * useLiveField.js — live, Google-Sheets-style field sync.
 *
 * Broadcasts a field's in-progress value to everyone else viewing the same
 * resource, over Supabase Realtime **Broadcast** (the same websocket the app
 * already uses). Broadcast is EPHEMERAL — it never writes to the database — so
 * this can NEVER cause data loss. Durability is still the caller's normal save
 * path (debounced whole-store persist / atomic RPC); this only powers the live
 * "watch it type" experience on top.
 *
 * Fail-safe by construction: if realtime is unavailable, the channel fails to
 * open, or the feature is disabled, `send` is a no-op and the caller keeps
 * working off its normal store-backed value — i.e. exactly today's behaviour.
 *
 *   const send = useLiveField(mockId ? 'mockup:' + mockId : null, onRemote);
 *   // on each keystroke, in addition to the normal durable update:
 *   send({ cellId, field: 'caption', value, from: myId, name });
 *
 * onRemote(payload) fires for messages from OTHER clients only (self is muted).
 */
import { useEffect, useRef, useCallback, useState } from 'react';
import { supabase } from './supabaseClient';

export function useLiveField(channelKey, onRemote, opts = {}) {
  const throttleMs = opts.throttleMs || 120;   // ~8 msgs/s max; stays under the
                                               // default realtime rate limit and
                                               // coalesces to the latest value.
  const chanRef = useRef(null);
  const onRemoteRef = useRef(onRemote);
  onRemoteRef.current = onRemote;

  const lastSent = useRef(0);
  const pending = useRef(null);
  const timer = useRef(null);

  useEffect(() => {
    if (!channelKey) return undefined;
    let alive = true;
    let channel = null;
    try {
      channel = supabase.channel('live:' + channelKey, { config: { broadcast: { self: false } } });
      channel.on('broadcast', { event: 'field' }, (msg) => {
        if (!alive) return;
        try { if (onRemoteRef.current) onRemoteRef.current(msg.payload); } catch (e) { /* ignore */ }
      });
      channel.subscribe();
      chanRef.current = channel;
    } catch (e) {
      chanRef.current = null;   // fail-safe: no live layer, callers still work
    }
    return () => {
      alive = false;
      if (timer.current) { clearTimeout(timer.current); timer.current = null; }
      pending.current = null;
      try { if (channel) supabase.removeChannel(channel); } catch (e) { /* ignore */ }
      chanRef.current = null;
    };
  }, [channelKey]);

  const flush = useCallback(() => {
    timer.current = null;
    const payload = pending.current;
    pending.current = null;
    if (!payload || !chanRef.current) return;
    lastSent.current = Date.now();
    try { chanRef.current.send({ type: 'broadcast', event: 'field', payload }); } catch (e) { /* ignore */ }
  }, []);

  // Coalescing throttle: always sends the LATEST value, at most once/throttleMs.
  const send = useCallback((payload) => {
    if (!chanRef.current) return;                 // fail-safe no-op
    pending.current = payload;
    if (timer.current) return;                    // a flush is already scheduled
    const wait = Math.max(0, throttleMs - (Date.now() - lastSent.current));
    if (wait === 0) flush(); else timer.current = setTimeout(flush, wait);
  }, [flush, throttleMs]);

  return send;
}

/**
 * useLiveCaptions — live, per-cell caption co-editing for one mockup.
 *
 * Broadcasts this tab's caption keystrokes and tracks other people's in-progress
 * caption text per cell, with a freshness expiry so a live value never lingers
 * after someone stops typing or closes their tab. Purely additive and fail-safe:
 * if `channelKey` is null (feature off) or realtime is unavailable, `sendCaption`
 * is a no-op and `remoteFor` returns null — so the caller renders its normal
 * store-backed caption, i.e. exactly today's behaviour.
 *
 *   const { sendCaption, remoteFor } = useLiveCaptions(on ? 'mockup:'+id : null, me);
 *   const rc = focusedCell !== cell.id ? remoteFor(cell.id) : null;   // someone else typing?
 *   <textarea value={rc ? rc.value : cell.caption}
 *             onChange={e => { updateCell(...); sendCaption(cell.id, e.target.value); }} />
 */
export function useLiveCaptions(channelKey, me) {
  const [remote, setRemote] = useState({});   // { cellId: { value, name, from, at } }

  const send = useLiveField(channelKey, useCallback((p) => {
    if (!p || p.field !== 'caption' || !p.cellId) return;
    setRemote(prev => ({ ...prev, [p.cellId]: { value: p.value, name: p.name, from: p.from, at: Date.now() } }));
  }, []));

  // Expire stale entries so an overlay never sticks. Runs only while entries exist
  // (no idle re-renders), and only re-renders when it actually drops one.
  const hasEntries = Object.keys(remote).length > 0;
  useEffect(() => {
    if (!hasEntries) return undefined;
    const iv = setInterval(() => {
      setRemote(prev => {
        const now = Date.now(); let changed = false; const next = {};
        for (const k in prev) { if (now - prev[k].at < 6000) next[k] = prev[k]; else changed = true; }
        return changed ? next : prev;
      });
    }, 1500);
    return () => clearInterval(iv);
  }, [hasEntries]);

  const myId = me && me.id;
  const myName = me && me.name;
  const sendCaption = useCallback((cellId, value) => {
    send({ cellId, field: 'caption', value, from: myId, name: myName });
  }, [send, myId, myName]);

  const remoteFor = useCallback((cellId) => {
    const r = remote[cellId];
    if (r && (Date.now() - r.at) < 6000 && r.from !== myId) return r;
    return null;
  }, [remote, myId]);

  return { sendCaption, remoteFor };
}

export default useLiveField;
